import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  useTheme,
  Link,
} from '@mui/material';
import { LockResetOutlined, LoginOutlined, ArrowBackOutlined } from '@mui/icons-material';
import authService from '../services/authService';
import { extractErrorMessage } from '../utils/errorUtils';
import { validatePassword } from '../utils/passwordValidation';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import Logo from '../components/Logo';
import PasswordTextField from '../components/PasswordTextField';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Récupérer le token depuis l'URL
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Token de réinitialisation manquant. Veuillez utiliser le lien reçu par email.');
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Vérifier que le token existe
    if (!token) {
      setError('Token de réinitialisation manquant');
      return;
    }

    // Validation côté client
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    const validation = validatePassword(password);
    if (!validation.isValid) {
      setError(validation.errors[0] || 'Le mot de passe ne respecte pas les critères requis');
      return;
    }

    setIsLoading(true);

    try {
      const message = await authService.resetPassword({
        token,
        newPassword: password,
      });
      console.log('Réponse:', message);
      setSuccess(true);

      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(
        err,
        'Erreur lors de la réinitialisation du mot de passe'
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'white',
            border: `3px solid ${theme.palette.secondary.main}`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo size={80} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Réinitialiser le mot de passe
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {success ? 'Mot de passe modifié !' : 'Choisissez un nouveau mot de passe sécurisé'}
            </Typography>
          </Box>

          {success ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Mot de passe réinitialisé avec succès !</strong>
                </Typography>
                <Typography variant="body2">
                  Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Redirection automatique vers la connexion...
                </Typography>
              </Alert>

              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                fullWidth
                startIcon={<LoginOutlined />}
                sx={{ mt: 2 }}
              >
                Se connecter maintenant
              </Button>
            </>
          ) : (
            <>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  action={
                    error.includes('Token') ||
                    error.includes('token') ||
                    error.includes('expiré') ? (
                      <Button
                        component={RouterLink}
                        to="/forgot-password"
                        size="small"
                        color="inherit"
                      >
                        Renvoyer
                      </Button>
                    ) : undefined
                  }
                >
                  {error}
                </Alert>
              )}

              {!token ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="error" gutterBottom>
                    Lien invalide ou expiré
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/forgot-password"
                    variant="outlined"
                    sx={{ mt: 2 }}
                  >
                    Demander un nouveau lien
                  </Button>
                </Box>
              ) : (
                <form onSubmit={handleSubmit}>
                  <PasswordTextField
                    fullWidth
                    label="Nouveau mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    margin="normal"
                    autoFocus
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem',
                      },
                    }}
                  />

                  <PasswordStrengthIndicator password={password} />

                  <PasswordTextField
                    fullWidth
                    label="Confirmer le mot de passe"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    margin="normal"
                    error={confirmPassword !== '' && password !== confirmPassword}
                    helperText={
                      confirmPassword !== '' && password !== confirmPassword
                        ? 'Les mots de passe ne correspondent pas'
                        : ''
                    }
                    sx={{
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem',
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading || !token}
                    startIcon={<LockResetOutlined />}
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                    }}
                  >
                    {isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                  </Button>
                </form>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <ArrowBackOutlined fontSize="small" />
                  Retour à la connexion
                </Link>
              </Box>
            </>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Sersal - Gestion de devis et facturation
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
